import { Button, Drawer, Flex, Select, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getAttachmentsFilterOptions } from "../../Album/api/attachment.services.ts"
import { type AttachmentFilterOptions } from "../../Album/types/attachment.types.ts"
import {
  EMPTY_FILES_FILTERS,
  type FilesFilters
} from "../types/files-filters.types.ts"

const { Text } = Typography

interface FilesFilterDrawerProps {
  open: boolean
  filters: FilesFilters
  showRelatedFilter: boolean
  onClose: () => void
  onChange: (filters: FilesFilters) => void
}

export function FilesFilterDrawer({
  open,
  filters,
  showRelatedFilter,
  onClose,
  onChange
}: FilesFilterDrawerProps) {
  const { t } = useTranslation()
  const [options, setOptions] = useState<AttachmentFilterOptions | null>(null)

  useEffect(() => {
    if (!open || options) return
    let cancelled = false
    void getAttachmentsFilterOptions().then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setOptions(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [open, options])

  const set = (key: keyof FilesFilters) => (value: string | undefined) =>
    onChange({ ...filters, [key]: value ?? null })

  const sections: Array<{
    key: keyof FilesFilters
    label: string
    options: AttachmentFilterOptions[keyof AttachmentFilterOptions] | undefined
    visible: boolean
  }> = [
    {
      key: "file_type",
      label: t("files.filters.fileType"),
      options: options?.file_types,
      visible: true
    },
    {
      key: "model_type",
      label: t("files.filters.relatedTo"),
      options: options?.model_types,
      visible: showRelatedFilter
    },
    {
      key: "date_range",
      label: t("files.filters.date"),
      options: options?.date_ranges,
      visible: true
    }
  ]

  const activeCount = Object.values(filters).filter((value) => value).length

  return (
    <Drawer
      title={t("files.filters.title")}
      placement="right"
      width={360}
      open={open}
      onClose={onClose}
      extra={
        activeCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={() => onChange(EMPTY_FILES_FILTERS)}
          >
            {t("files.filters.clearAll")}
          </Button>
        )
      }
    >
      <Flex vertical gap="large">
        {sections
          .filter((section) => section.visible)
          .map((section) => (
            <Flex vertical gap={8} key={section.key}>
              <Text strong>{section.label}</Text>
              <Select<string>
                allowClear
                aria-label={section.label}
                placeholder={t("files.filters.any")}
                value={filters[section.key] ?? undefined}
                onChange={set(section.key)}
                options={section.options ?? []}
                className="w-full"
              />
            </Flex>
          ))}
      </Flex>
    </Drawer>
  )
}
